import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "./useApi";

// Loads GET /soc/trends?days=N for the Security Operations Overview.
// `trends` is undefined until the first response, null when a read failed, else the response.
// `reloadKey` (Refresh) reloads normally; `pollKey` (the page's automatic refresh) reloads silently:
// the current figures stay on screen, an auth error never redirects, and a failure keeps them.
// Only the latest request may update state, so a slow 7-day response arriving after a 90-day one is
// discarded. A poll tick is skipped while any read is still running: superseding it would drop that
// read's answer, and a foreground read's loading state with it.
export const useSocTrends = ({ days, reloadKey = 0, pollKey = 0, enabled = true }) => {
  const apiCall = useApi();
  const [trends, setTrends] = useState(undefined);
  const [loading, setLoading] = useState(enabled);
  const seqRef = useRef(0);
  const inFlightRef = useRef(0);

  const load = useCallback(async (silent) => {
    if (silent && inFlightRef.current > 0) return;
    seqRef.current += 1;
    const seq = seqRef.current;
    inFlightRef.current += 1;
    if (!silent) setLoading(true);
    try {
      const data = await apiCall("GET", `/soc/trends?days=${days}`, null, false, null, silent);
      if (seq !== seqRef.current) return;
      setTrends(data || null);
    } catch {
      if (seq !== seqRef.current) return;
      if (!silent) setTrends(null);
    } finally {
      inFlightRef.current -= 1;
      if (seq === seqRef.current && !silent) setLoading(false);
    }
  }, [apiCall, days]);

  useEffect(() => {
    if (enabled) load(false);
  }, [enabled, load, reloadKey]);

  useEffect(() => {
    if (enabled && pollKey > 0) load(true);
    // Only a new poll tick triggers a silent load; days and Refresh reload through the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollKey]);

  return { trends, loading, reload: () => load(false) };
};

export default useSocTrends;
